import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Row, Col, Card, Table,
    Button, Form, Alert, Modal, Spinner, Badge
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { convertToLocalTime } from '../Backend/BackgroundTasks/convertutclocal';
import { useTransactionDetails } from './useTransactionDetails';
import { LineItemModal } from './LineItemModal';
import { CreateLineItemModal } from './CreateLineItemModal';
import { TicketHistoryModal } from './TicketHistoryModal';
import { CollectPaymentModal } from './CollectPaymentModal';
import { LayawayWizard } from './LayawayWizard';
import { downloadInvoicePDF, getReceiptBlob } from './generateTransactionPDF';
import { STRIPE_PAYMENTS_ENABLED } from '../featureFlags';

const hasValue = (value) =>
    value !== null &&
    value !== undefined &&
    !(typeof value === 'string' && value.trim() === '');

const formatCurrency = (amount) =>
    new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Number(amount ?? 0));

const formatDateTime = (value, t) => {
    if (!value) return t('N/A');
    return String(convertToLocalTime(value));
};

/** Convert a UTC ISO string → datetime-local value (YYYY-MM-DDTHH:MM in local time) */
const toDateTimeLocal = (utcStr) => {
    if (!utcStr) return '';
    const d = new Date(utcStr);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── Status configuration ──────────────────────────────────────────────────────

const SALE_NEXT_BUTTON = {
    New: { label: 'Finalize Transaction', nextStatus: 'Finalized', variant: 'warning' },
};

const REPAIR_NEXT_BUTTON = {
    New: { label: 'Mark Estimate Ready', nextStatus: 'Estimate Ready', variant: 'info' },
    'Estimate Ready': { label: 'Add to Queue', nextStatus: 'In Queue', variant: 'info' },
    'In Queue': { label: 'Move to Workbench', nextStatus: 'On the Workbench', variant: 'primary' },
    'On the Workbench': { label: 'Repair Complete', nextStatus: 'Ready for Pickup', variant: 'warning' },
};

const STATUS_BADGE_VARIANT = {
    New: 'secondary',
    'Estimate Ready': 'info',
    'In Queue': 'primary',
    'On the Workbench': 'warning',
    'Ready for Pickup': 'warning',
    Finalized: 'warning',
    Completed: 'success',
    'Picked Up': 'success',
    Void: 'danger',
};

export const TransactionDetails = ({ credentials }) => {
    const { t } = useTranslation();

    const {
        type, transactionId, navigate,
        customer, transaction, lineItems, payments,
        layawayOption, setLayawayOption,
        isPageLoading, pageError,
        isCustomerLoading, isLineItemsLoading, isPaymentsLoading,
        ticketHistory, hasMoreHistory,
        isHistoryLoading, isLoadingMoreHistory, historyError,
        fetchInitialTicketHistory, fetchMoreTicketHistory,
        saveLineItem, deleteLineItem, createLineItem,
        uploadLineItemImage, deleteUploadedImage,
        updateStatus, recordPayment,
        updateTransactionDetails, voidTransaction,
        generateLayawayContract, getLayawayContractUrl, getLayawayCheckoutUrl,
        createInStoreCheckout, createZettlePayment, pollForPayment,
        uploadReceipt, getReceiptDownloadUrl, refreshTransaction,
        notifyCustomer,
    } = useTransactionDetails();

    const [showHistory, setShowHistory] = useState(false);
    const [editingLineItem, setEditingLineItem] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isVoiding, setIsVoiding] = useState(false);
    const [statusError, setStatusError] = useState(null);

    // ── Layaway wizard ────────────────────────────────────────────────────────
    const [showLayawayWizard, setShowLayawayWizard] = useState(false);
    const [isDownloadingContract, setIsDownloadingContract] = useState(false);
    const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);

    // ── Receipt PDF (auto-generate on completion) ─────────────────────────────
    const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
    const [receiptUploadError, setReceiptUploadError] = useState(null);
    const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
    // Prevent duplicate upload attempts within the same component lifecycle
    const receiptUploadInProgressRef = useRef(false);

    // ── Notify Customer (repair ready-for-pickup SMS) ─────────────────────────
    const [isNotifyingCustomer, setIsNotifyingCustomer] = useState(false);
    const [notifyResult, setNotifyResult]               = useState(null); // { smsSent, reason }
    const [notifyError, setNotifyError]                 = useState(null);

    // ── Repair Complete confirmation modal ────────────────────────────────────
    const [showRepairCompleteConfirm, setShowRepairCompleteConfirm] = useState(false);

    // ── Create Line warning when repair is Ready for Pickup ───────────────────
    const [showCreateLineWarning, setShowCreateLineWarning] = useState(false);

    // ── Notify Customer confirmation modal ────────────────────────────────────
    const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);

    // ── Pay Online modal ──────────────────────────────────────────────────────
    const [showPayOnlineModal, setShowPayOnlineModal] = useState(false);
    const [payOnlineAmount, setPayOnlineAmount] = useState('');

    // ── Inline details editing ────────────────────────────────────────────────
    const [detailsNotes, setDetailsNotes] = useState('');
    const [detailsPriority, setDetailsPriority] = useState(3);
    const [detailsPromisedDate, setDetailsPromisedDate] = useState('');
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [detailsSaveError, setDetailsSaveError] = useState(null);
    const [detailsDirty, setDetailsDirty] = useState(false);

    useEffect(() => {
        if (showHistory) fetchInitialTicketHistory();
    }, [showHistory, fetchInitialTicketHistory]);

    // ── Auto-generate receipt when a transaction first reaches its terminal status ──
    useEffect(() => {
        if (!transaction || isPageLoading) return;

        const isTerminalNow =
            (type === 'sale'   && transaction.transactionStatus === 'Completed') ||
            (type === 'repair' && transaction.transactionStatus === 'Picked Up');

        // Only run if terminal, no receipt key yet, and not already in progress
        if (!isTerminalNow || transaction.receiptKey || receiptUploadInProgressRef.current) return;

        receiptUploadInProgressRef.current = true;
        setIsUploadingReceipt(true);
        setReceiptUploadError(null);

        (async () => {
            try {
                const blob = getReceiptBlob({ transaction, customer, lineItems, payments, type });
                await uploadReceipt(blob);
                await refreshTransaction(); // pulls updated receiptKey from DynamoDB
            } catch (e) {
                console.error('Receipt auto-generation failed:', e);
                setReceiptUploadError(e?.message || t('Failed to generate receipt'));
            } finally {
                setIsUploadingReceipt(false);
                receiptUploadInProgressRef.current = false;
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transaction?.transactionStatus, transaction?.receiptKey, isPageLoading]);

    // Sync detail fields whenever transaction loads or refreshes
    useEffect(() => {
        if (!transaction) return;
        setDetailsNotes(transaction.transactionNotes || '');
        setDetailsPriority(transaction.priority ?? 3);
        setDetailsPromisedDate(toDateTimeLocal(transaction.promisedDate));
        setDetailsDirty(false);
    }, [transaction]);

    if (isPageLoading) {
        return (
            <Modal show centered>
                <Modal.Body>
                    <div className="text-center">
                        <h3>{t('Getting Transaction Details')}</h3>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                        <Spinner animation="border" />
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    if (pageError || !transaction) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">
                    <Alert.Heading>{t('Unable to load transaction')}</Alert.Heading>
                    <p>{t('There was a problem loading the transaction details.')}</p>
                </Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    {t('Go Back')}
                </Button>
            </Container>
        );
    }

    // ── Derived state ─────────────────────────────────────────────────────────

    const status = transaction.transactionStatus || 'New';
    const balanceDue = transaction.balanceDue ?? 0;
    const isSale = type === 'sale';
    const isRepair = type === 'repair';

    // Terminal statuses — no further workflow actions allowed
    const TERMINAL = ['Completed', 'Picked Up', 'Void'];
    const isTerminal = TERMINAL.includes(status);

    // Line items locked only when the transaction is fully done
    const lineItemsLocked = isTerminal;

    // Show "Collect Payment" button
    const showCollectPayment =
        credentials.isEmployee &&
        ((isSale && status === 'Finalized') || (isRepair && status === 'Ready for Pickup'));

    // Show "Edit Sale" (revert to New) only when Finalized and no payments yet
    const showEditSale =
        credentials.isEmployee &&
        isSale &&
        status === 'Finalized' &&
        payments.length === 0;

    // Show Layaway toggle only for finalized sales (so the contract can be drafted)
    const showLayaway = credentials.isEmployee && isSale && status === 'Finalized';
    const showLayawayCustomer = credentials.isCustomer && isSale && status === 'Finalized';

    // Show "Confirm Pickup" for repairs at Ready for Pickup
    const showConfirmPickup = credentials.isEmployee && isRepair && status === 'Ready for Pickup';

    // Show "Notify Customer" button for repairs at Ready for Pickup (employee only)
    const showNotifyCustomer = credentials.isEmployee && isRepair && status === 'Ready for Pickup';

    // Repair backward navigation — step back one status, not from terminal states
    const REPAIR_STATUS_ORDER_UI = ['New', 'Estimate Ready', 'In Queue', 'On the Workbench', 'Ready for Pickup', 'Picked Up'];
    const repairCurrentIdx = REPAIR_STATUS_ORDER_UI.indexOf(status);
    const repairPrevStatus = isRepair && repairCurrentIdx > 0 ? REPAIR_STATUS_ORDER_UI[repairCurrentIdx - 1] : null;
    const showGoBack =
        credentials.isEmployee &&
        isRepair &&
        repairPrevStatus !== null &&
        !isTerminal;

    // Void — available on any non-terminal transaction
    const showVoid = credentials.isEmployee && !isTerminal;

    // Next-status button for this transaction type
    const nextBtn = isSale ? SALE_NEXT_BUTTON[status] : REPAIR_NEXT_BUTTON[status];

    // PDF download visibility — available to both employees and customers
    const showInvoice = balanceDue > 0 && status !== 'Void';
    const showReceipt = (isSale && status === 'Completed') || (isRepair && status === 'Picked Up');

    const customerName =
        transaction.customerName ||
        (customer
            ? customer.isBusiness
                ? customer.firstName || t('N/A')
                : `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || t('N/A')
            : t('N/A'));

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleStatusChange = async (nextStatus) => {
        setIsUpdatingStatus(true);
        setStatusError(null);
        try {
            await updateStatus(nextStatus);
        } catch (e) {
            setStatusError(
                e?.response?.data || e?.message || t('Failed to update status')
            );
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleVoid = async () => {
        if (!window.confirm(t('Are you sure you want to void this transaction? This cannot be undone.'))) return;
        setIsVoiding(true);
        setStatusError(null);
        try {
            await voidTransaction();
        } catch (e) {
            setStatusError(e?.response?.data || e?.message || t('Failed to void transaction'));
        } finally {
            setIsVoiding(false);
        }
    };

    // ── Layaway handlers ──────────────────────────────────────────────────────

    const handleLayawayWizardSubmit = async (payload) => {
        await generateLayawayContract(payload);
        setShowLayawayWizard(false);
        setLayawayOption(true);
    };

    const handleDownloadContract = async () => {
        setIsDownloadingContract(true);
        try {
            const url = await getLayawayContractUrl();
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            setStatusError(e?.response?.data || e?.message || t('Failed to download contract'));
        } finally {
            setIsDownloadingContract(false);
        }
    };

    // Opens the amount-selector modal; pre-fills with the installment amount.
    // maxOnlineAmount = balance - installment (the final installment is reserved for in-store).
    const handlePayOnline = () => {
        const installment = transaction?.layawayInstallmentAmount ?? 0;
        const balance = transaction?.balanceDue ?? 0;
        const maxOnline = installment > 0
            ? Math.max(0.50, balance - installment)
            : balance;
        // Pre-fill: installment, capped at maxOnline
        const prefill = Math.min(installment > 0 ? installment : balance, maxOnline);
        setPayOnlineAmount(String(prefill.toFixed(2)));
        setCheckoutError(null);
        setShowPayOnlineModal(true);
    };

    // Called when the user confirms the amount in the modal
    const handlePayOnlineConfirm = async () => {
        setIsLoadingCheckout(true);
        setCheckoutError(null);
        try {
            const amount = parseFloat(payOnlineAmount);
            const url = await getLayawayCheckoutUrl(amount);
            if (url) {
                window.location.href = url;  // same-window navigation to Stripe
            }
        } catch (e) {
            setCheckoutError(e?.response?.data || e?.message || t('Failed to create payment link'));
        } finally {
            setIsLoadingCheckout(false);
        }
    };

    // ── Receipt download handler ──────────────────────────────────────────────
    const handleDownloadReceipt = async () => {
        setIsDownloadingReceipt(true);
        try {
            const url = await getReceiptDownloadUrl();
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            setReceiptUploadError(e?.response?.data || e?.message || t('Failed to download receipt'));
        } finally {
            setIsDownloadingReceipt(false);
        }
    };

    // Employee can manually re-generate and re-upload if something went wrong
    const handleRegenerateReceipt = async () => {
        if (receiptUploadInProgressRef.current) return;
        receiptUploadInProgressRef.current = true;
        setIsUploadingReceipt(true);
        setReceiptUploadError(null);
        try {
            const blob = getReceiptBlob({ transaction, customer, lineItems, payments, type });
            await uploadReceipt(blob);
            await refreshTransaction();
        } catch (e) {
            setReceiptUploadError(e?.message || t('Failed to generate receipt'));
        } finally {
            setIsUploadingReceipt(false);
            receiptUploadInProgressRef.current = false;
        }
    };

    // ── Notify Customer handler ───────────────────────────────────────────────

    const handleNotifyCustomer = async () => {
        setIsNotifyingCustomer(true);
        setNotifyResult(null);
        setNotifyError(null);
        try {
            const result = await notifyCustomer();
            setNotifyResult(result);
        } catch (e) {
            setNotifyError(e?.response?.data || e?.message || t('Failed to send notification'));
        } finally {
            setIsNotifyingCustomer(false);
        }
    };

    // ── Details helpers ───────────────────────────────────────────────────────

    const handleSaveDetails = async () => {
        setIsSavingDetails(true);
        setDetailsSaveError(null);
        try {
            const payload = { transactionNotes: detailsNotes };
            if (isRepair) {
                payload.priority = detailsPriority;
                if (detailsPromisedDate) {
                    // Convert local datetime-local → UTC ISO string for the server
                    payload.promisedDate = new Date(detailsPromisedDate).toISOString();
                }
            }
            await updateTransactionDetails(payload);
            setDetailsDirty(false);
        } catch (e) {
            setDetailsSaveError(e?.response?.data || e?.message || t('Failed to save details'));
        } finally {
            setIsSavingDetails(false);
        }
    };

    const PRIORITY_OPTIONS = [
        { value: 1, label: '1 — Urgent' },
        { value: 2, label: '2 — High' },
        { value: 3, label: '3 — Standard' },
        { value: 4, label: '4 — Low' },
        { value: 5, label: '5 — Whenever' },
    ];

    const canEditDetails = credentials.isEmployee && status !== 'Void';

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-0">{t('Transaction Details')}</h4>
                        <small className="text-muted">
                            {t('Type')}: {type} | {t('Transaction ID')}: {transaction.transactionId}
                        </small>
                    </div>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        {t('Back')}
                    </Button>
                </Card.Header>

                <Card.Body>
                    {/* ── Customer + Summary ─────────────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>{t('Customer Information')}</Card.Header>
                                <Card.Body>
                                    {isCustomerLoading ? (
                                        <div className="d-flex justify-content-center py-3">
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    ) : (
                                        <>
                                            <p className="mb-2"><strong>{t('Customer Name')}:</strong> {customerName}</p>
                                            <p className="mb-2"><strong>{t('Customer ID')}:</strong> {transaction.customerId || t('N/A')}</p>
                                            <p className="mb-2"><strong>{t('Email')}:</strong> {customer?.email || t('N/A')}</p>
                                            <p className="mb-0"><strong>{t('Phone')}:</strong> {customer?.phoneNumber || customer?.phone || t('N/A')}</p>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>{t('Transaction Summary')}</Card.Header>
                                <Card.Body>
                                    {hasValue(transaction.ticketId) && (
                                        <p className="mb-2"><strong>{t('Ticket ID')}:</strong> {transaction.ticketId}</p>
                                    )}
                                    {hasValue(status) && (
                                        <p className="mb-2">
                                            <strong>{t('Status')}:</strong>{' '}
                                            <Badge bg={STATUS_BADGE_VARIANT[status] || 'secondary'}>
                                                {status}
                                            </Badge>
                                        </p>
                                    )}
                                    {hasValue(transaction.transactionDate) && (
                                        <p className="mb-2"><strong>{t('Opened On')}:</strong> {formatDateTime(transaction.transactionDate, t)}</p>
                                    )}
                                    {hasValue(transaction.employeeName) && (
                                        <p className="mb-2"><strong>{t('Opened By')}:</strong> {transaction.employeeName}</p>
                                    )}
                                    {hasValue(transaction.statusDate) && (
                                        <p className="mb-2"><strong>{t('Status Date')}:</strong> {formatDateTime(transaction.statusDate, t)}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Financials + Dates ─────────────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>{t('Financial Summary')}</Card.Header>
                                <Card.Body>
                                    {hasValue(transaction.transactionSubtotal) && (
                                        <p className="mb-2"><strong>{t('Subtotal')}:</strong> {formatCurrency(transaction.transactionSubtotal)}</p>
                                    )}
                                    {hasValue(transaction.transactionDiscount) && (
                                        <p className="mb-2"><strong>{t('Discount')}:</strong> {formatCurrency(transaction.transactionDiscount)}</p>
                                    )}
                                    {hasValue(transaction.transactionTax) && (
                                        <p className="mb-2"><strong>{t('Tax')}:</strong> {formatCurrency(transaction.transactionTax)}</p>
                                    )}
                                    {hasValue(transaction.transactionTotal) && (
                                        <p className="mb-2"><strong>{t('Total')}:</strong> {formatCurrency(transaction.transactionTotal)}</p>
                                    )}
                                    <p className="mb-2">
                                        <strong>{t('Balance Due')}:</strong>{' '}
                                        <span className={balanceDue > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                            {formatCurrency(balanceDue)}
                                        </span>
                                    </p>
                                    {hasValue(transaction.totalItems) && (
                                        <p className="mb-0"><strong>{t('Total Items')}:</strong> {transaction.totalItems}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>{t('Dates and Workflow')}</Card.Header>
                                <Card.Body>
                                    {hasValue(transaction.completedDate) && (
                                        <p className="mb-2"><strong>{t('Completed Date')}:</strong> {formatDateTime(transaction.completedDate, t)}</p>
                                    )}
                                    {hasValue(transaction.transactionVoidDate) && (
                                        <p className="mb-0"><strong>{t('Void Date')}:</strong> {formatDateTime(transaction.transactionVoidDate, t)}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Details (inline editable) ─────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card>
                                <Card.Header>{t('Details')}</Card.Header>
                                <Card.Body>
                                    {detailsSaveError && (
                                        <Alert variant="danger" dismissible onClose={() => setDetailsSaveError(null)} className="mb-3">
                                            {detailsSaveError}
                                        </Alert>
                                    )}

                                    <Row>
                                        {/* Priority — repair only */}
                                        {isRepair && (
                                            <Col md={3} className="mb-3">
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('Priority')}</Form.Label>
                                                    <Form.Select
                                                        value={detailsPriority}
                                                        disabled={!canEditDetails}
                                                        onChange={(e) => { setDetailsPriority(Number(e.target.value)); setDetailsDirty(true); }}
                                                    >
                                                        {PRIORITY_OPTIONS.map((o) => (
                                                            <option key={o.value} value={o.value}>{t(o.label)}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}

                                        {/* Promised Date — repair only, datetime-local in local time */}
                                        {isRepair && (
                                            <Col md={4} className="mb-3">
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('Promised Date & Time')}</Form.Label>
                                                    <Form.Control
                                                        type="datetime-local"
                                                        value={detailsPromisedDate}
                                                        disabled={!canEditDetails}
                                                        onChange={(e) => { setDetailsPromisedDate(e.target.value); setDetailsDirty(true); }}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {t('Displayed and entered in your local time')}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        )}

                                        {/* Transaction Notes — both types, fills remaining space */}
                                        <Col className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">{t('Transaction Notes')}</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    value={detailsNotes}
                                                    disabled={!canEditDetails}
                                                    placeholder={canEditDetails ? t('Add notes…') : ''}
                                                    onChange={(e) => { setDetailsNotes(e.target.value); setDetailsDirty(true); }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {canEditDetails && detailsDirty && (
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleSaveDetails}
                                                disabled={isSavingDetails}
                                            >
                                                {isSavingDetails ? t('Saving…') : t('Save Details')}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={isSavingDetails}
                                                onClick={() => {
                                                    setDetailsNotes(transaction.transactionNotes || '');
                                                    setDetailsPriority(transaction.priority ?? 3);
                                                    setDetailsPromisedDate(toDateTimeLocal(transaction.promisedDate));
                                                    setDetailsDirty(false);
                                                }}
                                            >
                                                {t('Discard')}
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Line Items ─────────────────────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card>
                                <Card.Header>{t('Line Items')}</Card.Header>
                                <Card.Body>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th>{t('Line #')}</th>
                                                <th>{t('Title')}</th>
                                                <th>{t('Description')}</th>
                                                <th>{t('Material')}</th>
                                                <th>{t('Quantity')}</th>
                                                <th>{t('Cost')}</th>
                                                <th>{t('Discount')}</th>
                                                <th>{t('Tax Exempt')}</th>
                                                <th>{t('Weigh In (g)')}</th>
                                                <th>{t('Weigh Out (g)')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLineItemsLoading ? (
                                                <tr>
                                                    <td colSpan={10} className="text-center py-3">
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        {t('Loading line items…')}
                                                    </td>
                                                </tr>
                                            ) : lineItems.length > 0 ? (
                                                lineItems.map((item) => (
                                                    <tr
                                                        key={item.id}
                                                        onClick={() => setEditingLineItem({ ...item })}
                                                        style={{ cursor: 'pointer' }}
                                                        title={t('Click to view details')}
                                                    >
                                                        <td>{item.id}</td>
                                                        <td>{item.title || t('N/A')}</td>
                                                        <td>{item.description || t('N/A')}</td>
                                                        <td>{item.material || t('N/A')}</td>
                                                        <td>{hasValue(item.quantity) ? item.quantity : 1}</td>
                                                        <td>{hasValue(item.cost) ? formatCurrency(item.cost) : t('N/A')}</td>
                                                        <td>{hasValue(item.discountAmount) ? formatCurrency(item.discountAmount) : t('N/A')}</td>
                                                        <td>{item.taxExempt ? t('Yes') : t('No')}</td>
                                                        <td>{hasValue(item.weighIn) ? item.weighIn : t('N/A')}</td>
                                                        <td>{hasValue(item.weighOut) ? item.weighOut : t('N/A')}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={10} className="text-center">
                                                        {t('No line items found')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Payments ───────────────────────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card>
                                <Card.Header>{t('Payments')}</Card.Header>
                                <Card.Body>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th>{t('Date')}</th>
                                                <th>{t('Method')}</th>
                                                <th>{t('Base Amount')}</th>
                                                <th>{t('Platform Fee')}</th>
                                                <th>{t('Amount Charged')}</th>
                                                <th>{t('Card Last 4')}</th>
                                                <th>{t('Auth #')}</th>
                                                <th>{t('Zettle Ref')}</th>
                                                <th>{t('Employee')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isPaymentsLoading ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center py-3">
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        {t('Loading payments…')}
                                                    </td>
                                                </tr>
                                            ) : payments.length > 0 ? (
                                                payments.map((p, idx) => (
                                                    <tr key={p.paymentId || idx}>
                                                        <td>{formatDateTime(p.paymentDate, t)}</td>
                                                        <td>{p.paymentMethod || t('N/A')}</td>
                                                        <td>{formatCurrency(p.baseAmount ?? p.amount)}</td>
                                                        <td>
                                                            {p.ccFeeApplied
                                                                ? formatCurrency(p.ccFeeAmount)
                                                                : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="fw-bold">{formatCurrency(p.amount)}</td>
                                                        <td>{p.lastFour ? `••••${p.lastFour}` : <span className="text-muted">—</span>}</td>
                                                        <td>{p.authorizationNumber || <span className="text-muted">—</span>}</td>
                                                        <td>{p.zettleReference || <span className="text-muted">—</span>}</td>
                                                        <td>{p.employeeName || t('N/A')}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={9} className="text-center">
                                                        {t('No payments found')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Options ────────────────────────────────────────────── */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card>
                                <Card.Header>{t('Options')}</Card.Header>
                                <Card.Body>
                                    {statusError && (
                                        <Alert
                                            variant="danger"
                                            dismissible
                                            onClose={() => setStatusError(null)}
                                            className="mb-3"
                                        >
                                            {statusError}
                                        </Alert>
                                    )}

                                    {/* Notify Customer feedback */}
                                    {notifyError && (
                                        <Alert
                                            variant="danger"
                                            dismissible
                                            onClose={() => setNotifyError(null)}
                                            className="mb-3"
                                        >
                                            {t('SMS notification failed')}: {notifyError}
                                        </Alert>
                                    )}
                                    {notifyResult && notifyResult.smsSent && (
                                        <Alert
                                            variant="success"
                                            dismissible
                                            onClose={() => setNotifyResult(null)}
                                            className="mb-3"
                                        >
                                            {t('SMS sent successfully to customer.')}
                                        </Alert>
                                    )}
                                    {notifyResult && !notifyResult.smsSent && (
                                        <Alert
                                            variant="warning"
                                            dismissible
                                            onClose={() => setNotifyResult(null)}
                                            className="mb-3"
                                        >
                                            {notifyResult.reason === 'no_phone'
                                                ? t('Customer has no phone number on file — SMS not sent.')
                                                : t('No customer linked to this repair — SMS not sent.')}
                                        </Alert>
                                    )}

                                    {/* Layaway section — only for Finalized sales */}
                                    {showLayaway && (
                                        <div className="mb-3">
                                            {transaction.layawayContractKey ? (
                                                // Contract already generated — show download + pay-online buttons
                                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                                    <Badge bg="success" className="fs-6 px-3 py-2">
                                                        ✅ {t('Layaway Active')}
                                                    </Badge>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={handleDownloadContract}
                                                        disabled={isDownloadingContract}
                                                    >
                                                        {isDownloadingContract
                                                            ? <><Spinner size="sm" animation="border" className="me-1" />{t('Loading…')}</>
                                                            : `📄 ${t('Download Contract')}`
                                                        }
                                                    </Button>
                                                    {STRIPE_PAYMENTS_ENABLED && transaction.layawayAllowOnlinePayments && (() => {
                                                        const installment = transaction.layawayInstallmentAmount ?? 0;
                                                        const balance = balanceDue;
                                                        const finalOnly = installment > 0 && balance <= installment;
                                                        return finalOnly ? (
                                                            <Alert variant="warning" className="mb-0 py-1 px-2 small">
                                                                🏪 {t('Final payment of')} {formatCurrency(balance)} {t('must be paid in-store at pickup')}
                                                            </Alert>
                                                        ) : (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={handlePayOnline}
                                                                title={t('Choose a payment amount and open Stripe checkout')}
                                                            >
                                                                💳 {t('Pay Online')}
                                                            </Button>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                // No contract yet — show toggle to open wizard
                                                <Form>
                                                    <Form.Check
                                                        type="switch"
                                                        id="layaway-switch"
                                                        label={t('Layaway Option')}
                                                        checked={layawayOption}
                                                        onChange={() => {
                                                            if (!layawayOption) {
                                                                setShowLayawayWizard(true);
                                                                setLayawayOption(true);
                                                            } else {
                                                                setLayawayOption(false);
                                                            }
                                                        }}
                                                    />
                                                    {layawayOption && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => setShowLayawayWizard(true)}
                                                        >
                                                            {t('Set Up Layaway Contract')} →
                                                        </Button>
                                                    )}
                                                </Form>
                                            )}
                                        </div>
                                    )}
                                    {showLayawayCustomer && !credentials.isEmployee && (
                                        <div className="mb-3">
                                            {transaction.layawayContractKey ? (
                                                // Contract already generated — show download + pay-online buttons
                                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                                    <Badge bg="success" className="fs-6 px-3 py-2">
                                                        ✅ {t('Layaway Active')}
                                                    </Badge>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={handleDownloadContract}
                                                        disabled={isDownloadingContract}
                                                    >
                                                        {isDownloadingContract
                                                            ? <><Spinner size="sm" animation="border" className="me-1" />{t('Loading…')}</>
                                                            : `📄 ${t('Download Contract')}`
                                                        }
                                                    </Button>
                                                    {STRIPE_PAYMENTS_ENABLED && transaction.layawayAllowOnlinePayments && (() => {
                                                        const installment = transaction.layawayInstallmentAmount ?? 0;
                                                        const balance = balanceDue;
                                                        const finalOnly = installment > 0 && balance <= installment;
                                                        return finalOnly ? (
                                                            <Alert variant="warning" className="mb-0 py-1 px-2 small">
                                                                🏪 {t('Final payment of')} {formatCurrency(balance)} {t('must be paid in-store at pickup')}
                                                            </Alert>
                                                        ) : (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={handlePayOnline}
                                                                title={t('Choose a payment amount and open Stripe checkout')}
                                                            >
                                                                💳 {t('Pay Online')}
                                                            </Button>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                // No contract yet — show toggle to open wizard
                                                <Form>

                                                </Form>
                                            )}
                                        </div>
                                    )}
                                    <div className="d-flex gap-2 mt-2 flex-wrap">
                                        {/* Create Line Item — locked only when transaction is fully done */}
                                        {credentials.isEmployee && (
                                            <Button
                                                variant="success"
                                                onClick={() => {
                                                    if (isRepair && status === 'Ready for Pickup') {
                                                        setShowCreateLineWarning(true);
                                                    } else {
                                                        setShowCreateModal(true);
                                                    }
                                                }}
                                                disabled={lineItemsLocked}
                                                title={lineItemsLocked
                                                    ? t('Line items cannot be added to a completed or voided transaction')
                                                    : undefined}
                                            >
                                                {t('Create Line')}
                                            </Button>
                                        )}

                                        {/* Next-status button (sale OR repair, up to Ready for Pickup) */}
                                        {credentials.isEmployee && nextBtn && (
                                            <Button
                                                variant={nextBtn.variant}
                                                onClick={() => {
                                                    // "Repair Complete" → Ready for Pickup requires confirmation
                                                    if (isRepair && nextBtn.nextStatus === 'Ready for Pickup') {
                                                        setShowRepairCompleteConfirm(true);
                                                    } else {
                                                        handleStatusChange(nextBtn.nextStatus);
                                                    }
                                                }}
                                                disabled={isUpdatingStatus}
                                            >
                                                {isUpdatingStatus ? t('Updating…') : t(nextBtn.label)}
                                            </Button>
                                        )}

                                        {/* Collect Payment */}
                                        {showCollectPayment && (
                                            <Button
                                                variant="success"
                                                onClick={() => setShowPaymentModal(true)}
                                            >
                                                {t('Collect Payment')}
                                            </Button>
                                        )}

                                        {/* Edit Sale — revert Finalized → New (only before any payments) */}
                                        {showEditSale && (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => handleStatusChange('New')}
                                                disabled={isUpdatingStatus}
                                                title={t('Revert to New to edit line items. Re-finalize when done.')}
                                            >
                                                {isUpdatingStatus ? t('Updating…') : t('Edit Sale')}
                                            </Button>
                                        )}

                                        {/* Go Back — repair only, steps back one status */}
                                        {showGoBack && (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => handleStatusChange(repairPrevStatus)}
                                                disabled={isUpdatingStatus}
                                                title={`${t('Revert to')} "${repairPrevStatus}"`}
                                            >
                                                {isUpdatingStatus ? t('Updating…') : `← ${t('Go Back')}`}
                                            </Button>
                                        )}

                                        {/* Confirm Pickup — disabled until balance = $0 */}
                                        {showConfirmPickup && (
                                            <Button
                                                variant="primary"
                                                onClick={() => handleStatusChange('Picked Up')}
                                                disabled={balanceDue > 0 || isUpdatingStatus}
                                                title={balanceDue > 0
                                                    ? t('Cannot confirm pickup until balance due is $0.00')
                                                    : undefined}
                                            >
                                                {t('Confirm Pickup')}
                                            </Button>
                                        )}

                                        {/* Notify Customer — repair + Ready for Pickup only */}
                                        {showNotifyCustomer && (
                                            <Button
                                                variant="outline-warning"
                                                onClick={() => setShowNotifyConfirm(true)}
                                                disabled={isNotifyingCustomer}
                                                title={t('Send SMS to customer that their repair is ready for pickup')}
                                            >
                                                {isNotifyingCustomer
                                                    ? <><Spinner size="sm" animation="border" className="me-1" />{t('Sending…')}</>
                                                    : <>📱 {t('Notify Customer')}</>
                                                }
                                            </Button>
                                        )}

                                        {/* Download Invoice (always client-side) */}
                                        {showInvoice && (
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => downloadInvoicePDF({ transaction, customer, lineItems, payments, type })}
                                                title={t('Download a PDF invoice showing the outstanding balance')}
                                            >
                                                📄 {t('Download Invoice')}
                                            </Button>
                                        )}

                                        {/* Download Receipt — served from S3 once generated */}
                                        {showReceipt && transaction.receiptKey && (
                                            <Button
                                                variant="outline-success"
                                                onClick={handleDownloadReceipt}
                                                disabled={isDownloadingReceipt}
                                                title={t('Download a PDF receipt confirming full payment')}
                                            >
                                                {isDownloadingReceipt
                                                    ? <><Spinner size="sm" animation="border" className="me-1" />{t('Loading…')}</>
                                                    : <>🧾 {t('Download Receipt')}</>
                                                }
                                            </Button>
                                        )}

                                        {/* Receipt generating (auto-upload in progress) */}
                                        {showReceipt && !transaction.receiptKey && isUploadingReceipt && (
                                            <Button variant="outline-secondary" disabled>
                                                <Spinner size="sm" animation="border" className="me-1" />
                                                {t('Generating receipt…')}
                                            </Button>
                                        )}

                                        {/* Receipt failed — employee can retry */}
                                        {showReceipt && !transaction.receiptKey && !isUploadingReceipt && receiptUploadError && credentials.isEmployee && (
                                            <Button
                                                variant="outline-warning"
                                                onClick={handleRegenerateReceipt}
                                                title={receiptUploadError}
                                            >
                                                ⚠️ {t('Retry Receipt')}
                                            </Button>
                                        )}

                                        {/* View History */}
                                        <Button variant="secondary" onClick={() => setShowHistory(true)}>
                                            {t('View Ticket History')}
                                        </Button>

                                        {/* Void Transaction */}
                                        {showVoid && (
                                            <Button
                                                variant="danger"
                                                onClick={handleVoid}
                                                disabled={isVoiding}
                                            >
                                                {isVoiding ? t('Voiding…') : t('Void Transaction')}
                                            </Button>
                                        )}

                                        {/* Customer-facing layaway payment */}
                                        {credentials.isCustomer &&
                                            isSale &&
                                            transaction.layawayContractKey &&
                                            transaction.layawayAllowOnlinePayments &&
                                            balanceDue > 0 && (() => {
                                                const installment = transaction.layawayInstallmentAmount ?? 0;
                                                const finalOnly = installment > 0 && balanceDue <= installment;
                                                return finalOnly ? (
                                                    <Alert variant="warning" className="mb-0 py-1 px-2 small">
                                                        🏪 {t('Your final payment of')} {formatCurrency(balanceDue)} {t('must be made in-store at pickup')}
                                                    </Alert>
                                                ) : (
                                                    <Button variant="success" onClick={handlePayOnline}>
                                                        💳 {t('Pay Online')}
                                                    </Button>
                                                );
                                            })()}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* ── Modals ───────────────────────────────────────────────────── */}
            <LineItemModal
                show={Boolean(editingLineItem)}
                lineItem={editingLineItem}
                type={type}
                transactionId={transactionId}
                onClose={() => setEditingLineItem(null)}
                onSave={saveLineItem}
                onDelete={deleteLineItem}
                onUploadImage={uploadLineItemImage}
                onDeleteImage={deleteUploadedImage}
                readOnly={
                    ['Finalized', 'Ready for Pickup', 'Completed', 'Picked Up', 'Void'].includes(status) &&
                    (editingLineItem?.cost ?? 0) > 0
                }
            />

            <CreateLineItemModal
                show={showCreateModal}
                type={type}
                transactionId={transactionId}
                onClose={() => setShowCreateModal(false)}
                onCreate={createLineItem}
                onUploadImage={uploadLineItemImage}
                onDeleteImage={deleteUploadedImage}
            />

            <TicketHistoryModal
                show={showHistory}
                ticketId={transaction.ticketId}
                history={ticketHistory}
                hasMore={hasMoreHistory}
                isLoading={isHistoryLoading}
                isLoadingMore={isLoadingMoreHistory}
                error={historyError}
                onLoadMore={fetchMoreTicketHistory}
                onClose={() => setShowHistory(false)}
            />

            <CollectPaymentModal
                show={showPaymentModal}
                balanceDue={balanceDue}
                transactionId={transactionId}
                ticketType={type}
                onClose={() => setShowPaymentModal(false)}
                onSubmit={recordPayment}
                onStripeCheckout={createInStoreCheckout}
                onZettleCheckout={createZettlePayment}
                onPollPayments={pollForPayment}
                initialPaymentCount={payments.length}
            />

            {/* ── Pay Online — amount selector modal ──────────────────────── */}
            <Modal
                show={showPayOnlineModal}
                onHide={() => setShowPayOnlineModal(false)}
                centered
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">💳 {t('Pay Online')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const installment = transaction?.layawayInstallmentAmount ?? 0;
                        const balance = transaction?.balanceDue ?? 0;
                        const plan = transaction?.layawayPaymentPlan || 'installment';
                        const passFee = Boolean(transaction?.layawayPassCcFees);
                        // Max allowed online = balance minus the final in-store installment
                        const maxOnline = installment > 0
                            ? Math.max(0.50, balance - installment)
                            : balance;
                        const amt = parseFloat(payOnlineAmount) || 0;
                        const minAmt = installment > 0 ? installment : 0.50;
                        const feeAmt = passFee ? amt * 0.03 : 0;
                        const totalAmt = amt + feeAmt;
                        const isValid = amt >= minAmt && amt <= maxOnline;

                        return (
                            <>
                                {/* Balance info */}
                                <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span>{t('Remaining balance')}:</span>
                                    <strong className="text-danger">{formatCurrency(balance)}</strong>
                                </div>
                                {installment > 0 && (
                                    <div className="d-flex justify-content-between small text-muted mb-1">
                                        <span>{t('Minimum')} ({plan}):</span>
                                        <strong>{formatCurrency(installment)}</strong>
                                    </div>
                                )}
                                {/* In-store reserve notice */}
                                {installment > 0 && (
                                    <div className="d-flex justify-content-between small mb-3">
                                        <span className="text-muted">🏪 {t('Reserved for in-store final payment')}:</span>
                                        <strong className="text-warning">{formatCurrency(installment)}</strong>
                                    </div>
                                )}

                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-semibold small mb-1">
                                        {t('Payment amount')}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={minAmt}
                                        max={maxOnline}
                                        step="0.01"
                                        value={payOnlineAmount}
                                        onChange={(e) => setPayOnlineAmount(e.target.value)}
                                        isInvalid={payOnlineAmount !== '' && !isValid}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {amt < minAmt
                                            ? `${t('Minimum payment is')} ${formatCurrency(minAmt)}`
                                            : `${t('Maximum online payment is')} ${formatCurrency(maxOnline)} (${t('final installment reserved for in-store')})`
                                        }
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* Quick-fill buttons */}
                                <div className="d-flex gap-2 mb-3">
                                    {installment > 0 && installment <= maxOnline && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="flex-fill"
                                            onClick={() => setPayOnlineAmount(installment.toFixed(2))}
                                        >
                                            {t('Installment')} ({formatCurrency(installment)})
                                        </Button>
                                    )}
                                    {maxOnline > 0 && maxOnline !== installment && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="flex-fill"
                                            onClick={() => setPayOnlineAmount(maxOnline.toFixed(2))}
                                        >
                                            {t('Max online')} ({formatCurrency(maxOnline)})
                                        </Button>
                                    )}
                                </div>

                                {/* Charge breakdown */}
                                {amt > 0 && isValid && (
                                    <Alert variant="info" className="py-2 px-3 small mb-2">
                                        <div className="d-flex justify-content-between">
                                            <span>{t('Payment amount')}:</span>
                                            <span>{formatCurrency(amt)}</span>
                                        </div>
                                        {passFee && (
                                            <div className="d-flex justify-content-between text-muted">
                                                <span>{t('Online platform fee (3%)')}:</span>
                                                <span>+{formatCurrency(feeAmt)}</span>
                                            </div>
                                        )}
                                        <div className="d-flex justify-content-between fw-bold border-top mt-1 pt-1">
                                            <span>{t('Total charged')}:</span>
                                            <span>{formatCurrency(totalAmt)}</span>
                                        </div>
                                    </Alert>
                                )}

                                {checkoutError && (
                                    <Alert variant="danger" className="py-2 px-3 small mb-0">
                                        {checkoutError}
                                    </Alert>
                                )}
                            </>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowPayOnlineModal(false)}
                        disabled={isLoadingCheckout}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="success"
                        size="sm"
                        onClick={handlePayOnlineConfirm}
                        disabled={
                            isLoadingCheckout ||
                            !payOnlineAmount ||
                            (() => {
                                const amt = parseFloat(payOnlineAmount) || 0;
                                const min = transaction?.layawayInstallmentAmount > 0
                                    ? transaction.layawayInstallmentAmount : 0.50;
                                const bal = transaction?.balanceDue ?? 0;
                                return amt < min || amt > bal;
                            })()
                        }
                    >
                        {isLoadingCheckout
                            ? <><Spinner size="sm" animation="border" className="me-1" />{t('Redirecting…')}</>
                            : `✓ ${t('Proceed to Checkout')}`
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            <LayawayWizard
                show={showLayawayWizard}
                onClose={() => {
                    setShowLayawayWizard(false);
                    if (!transaction.layawayContractKey) setLayawayOption(false);
                }}
                onSubmit={handleLayawayWizardSubmit}
                sale={transaction}
                customer={customer}
                lineItems={lineItems}
                employeeName={transaction?.employeeName || ''}
            />

            {/* ── Notify Customer confirmation ──────────────────────────── */}
            <Modal
                show={showNotifyConfirm}
                onHide={() => setShowNotifyConfirm(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>📱 {t('Notify Customer')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        ⚠️ {t('The customer was automatically notified when this repair was marked complete.')}{' '}
                        {t('Check')}{' '}
                        <span
                            role="button"
                            className="text-decoration-underline fw-semibold"
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setShowNotifyConfirm(false); setShowHistory(true); }}
                        >
                            {t('Ticket History')}
                        </span>{' '}
                        {t('to see when the last notification was sent.')}
                    </Alert>
                    <p className="mb-0 text-muted small">
                        {t('Sending another notification will text the customer that their repair is ready for pickup.')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowNotifyConfirm(false)}
                        disabled={isNotifyingCustomer}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={async () => {
                            setShowNotifyConfirm(false);
                            await handleNotifyCustomer();
                        }}
                        disabled={isNotifyingCustomer}
                    >
                        {isNotifyingCustomer
                            ? <><Spinner size="sm" animation="border" className="me-1" />{t('Sending…')}</>
                            : <>📱 {t('Notify Customer Anyway')}</>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Repair Complete confirmation ──────────────────────────── */}
            <Modal
                show={showRepairCompleteConfirm}
                onHide={() => setShowRepairCompleteConfirm(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('Mark Repair Complete?')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {t('This will mark the repair as')} <strong>{t('Ready for Pickup')}</strong>{' '}
                        {t('and automatically send an SMS notification to the customer that their repair is ready.')}
                    </p>
                    <Alert variant="info" className="mb-0 small">
                        📱 {t('The customer will receive a text message with the repair ticket number, balance due, and a link to their account.')}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRepairCompleteConfirm(false)}
                        disabled={isUpdatingStatus}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={async () => {
                            setShowRepairCompleteConfirm(false);
                            await handleStatusChange('Ready for Pickup');
                        }}
                        disabled={isUpdatingStatus}
                    >
                        {isUpdatingStatus
                            ? <><Spinner size="sm" animation="border" className="me-1" />{t('Updating…')}</>
                            : t('Confirm & Notify Customer')
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Create Line warning when repair is Ready for Pickup ───── */}
            <Modal
                show={showCreateLineWarning}
                onHide={() => setShowCreateLineWarning(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('Add Line Item?')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-0">
                        ⚠️ {t('Are you sure you want to add a line item? The total balance was already sent to the customer and may conflict with what they are expecting to pay.')}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCreateLineWarning(false)}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => {
                            setShowCreateLineWarning(false);
                            setShowCreateModal(true);
                        }}
                    >
                        {t('Add Anyway')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};
